import { Tooltip } from '@/shared/components/Tooltip'
import { formatDateTime } from '@/lib/format'

export interface BlockedBadgeProps {
  /** Badge text. Defaults to "blocked". */
  label?: string
  /** True when this is an admin manual block (vs a policy-driven block). */
  manual?: boolean
  /** Manual block reason, or null. */
  reason?: string | null
  /** Who applied the manual block (email/name), or null. */
  blockedByName?: string | null
  /** When the manual block was applied (unix ms), or null. */
  blockedAt?: number | null
  /** Comma-separated failing enforcing policy names (policy-driven block). */
  policySummary?: string | null
}

/**
 * Compact red "blocked" badge sized to match the security/severity badges, with a
 * hover/focus tooltip detailing why: manual reason + who/when, or the policy names
 * that are blocking. The trigger is a <button> (a11y + the surrounding row is
 * usually clickable) that stops click/keydown propagation so activating it does not
 * trigger the row's navigation.
 */
export function BlockedBadge({ label = 'blocked', manual = false, reason, blockedByName, blockedAt, policySummary }: BlockedBadgeProps) {
  const title = manual ? 'Manual block' : 'Policy block'

  // Label/value rows for the tabular tooltip body.
  const rows: Array<[string, string]> = []
  if (manual) {
    if (reason) rows.push(['Reason', reason])
    if (blockedByName) rows.push(['Blocked by', blockedByName])
    if (blockedAt) rows.push(['When', formatDateTime(blockedAt)])
  } else if (policySummary) {
    rows.push(['Failing policies', policySummary])
  } else if (reason) {
    // Generic reason (e.g. module list, where manual/policy is pre-resolved server-side).
    rows.push(['Reason', reason])
  }

  const aria = `${label}: ${title}${rows.length ? ' - ' + rows.map(([k, v]) => `${k} ${v}`).join('; ') : ''}`

  const badge = (
    <button
      type="button"
      className="badge badge-red"
      // Reset button chrome only - do NOT set font, so .badge font-size (0.6rem) wins
      // and the badge matches the severity badges next to it.
      style={{ cursor: 'help', margin: 0 }}
      aria-label={aria}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
    >
      {label}
    </button>
  )

  const tip = (
    <span style={{ display: 'grid', gap: 6, textAlign: 'left', minWidth: 180 }}>
      {/* Header */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: 'var(--red)', fontWeight: 600 }}>✗ {title}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)' }}>
          not consumable
        </span>
      </span>
      {/* Tabular label/value rows */}
      {rows.length > 0 && (
        <span style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 3, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
          {rows.map(([k, v]) => (
            <span key={k} style={{ display: 'contents' }}>
              <span style={{ color: 'var(--text3)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{k}</span>
              <span style={{ color: 'var(--text)', fontSize: '0.72rem', wordBreak: 'break-word' }}>{v}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  )

  return <Tooltip text={tip} placement="top">{badge}</Tooltip>
}
